import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import type { CustomBenchmarkImage, ListBenchmarkImagesQuery, ListBenchmarkImagesQueryVariables } from '@vizit/api';
import { useImageTags, useListBenchmarkImagesQuery } from '@vizit/api';
import { useInfiniteScroll } from '@vizit/hooks';
import { HEATMAP_ARTIFACT_TYPES, type Heatmap, heatmapLabel } from '@vizit/organisms';
import type { BenchmarkImageItem, BenchmarkImageStatus } from '@vizit/organisms';
import * as React from 'react';
import { ARTIFACT_URLS_REFRESH_AFTER_MS } from '../components/collection-asset-grid/collection-asset-grid.hooks';

/**
 * Benchmarks can hold more images than one request should carry, so the page reads them a page at a time as the
 * user scrolls, like the collection grid. With a product category each image carries its pair score and the API
 * caps the page at 100; the same size is used without one so the grid fills the same way either way.
 */
const BENCHMARK_IMAGES_PAGE_SIZE = 100; // score-threshold-allow: API page cap with a category, not a score

enum BenchmarkImageSortOption {
  Newest = 'newest',
  Oldest = 'oldest',
  Name = 'name',
  ScoreDesc = 'score_desc',
  ScoreAsc = 'score_asc',
}

const SORT_QUERY: Record<
  BenchmarkImageSortOption,
  Pick<ListBenchmarkImagesQueryVariables, 'sort_by' | 'sort_direction'>
> = {
  [BenchmarkImageSortOption.Newest]: { sort_by: 'created_at', sort_direction: 'desc' },
  [BenchmarkImageSortOption.Oldest]: { sort_by: 'created_at', sort_direction: 'asc' },
  [BenchmarkImageSortOption.Name]: { sort_by: 'name', sort_direction: 'asc' },
  // The API sorts unscored images last both ways, so the order holds across pages.
  [BenchmarkImageSortOption.ScoreDesc]: { sort_by: 'pair_score', sort_direction: 'desc' },
  [BenchmarkImageSortOption.ScoreAsc]: { sort_by: 'pair_score', sort_direction: 'asc' },
};

const SORT_OPTIONS = new Set<unknown>(Object.values(BenchmarkImageSortOption));

function parseBenchmarkImageSortOption(value: unknown): BenchmarkImageSortOption {
  return SORT_OPTIONS.has(value) ? (value as BenchmarkImageSortOption) : BenchmarkImageSortOption.Newest;
}

/** Whether the option sorts by the images' score under the selected product category. */
function isBenchmarkImageScoreSort(option: BenchmarkImageSortOption) {
  return option === BenchmarkImageSortOption.ScoreDesc || option === BenchmarkImageSortOption.ScoreAsc;
}

/**
 * Where an image is, as the page can tell: the API cannot report that an uploaded file has landed, so processing
 * means its upload is still running in this browser, and failed means that upload did not complete. With a product
 * category, an image whose scoring under it is still pending is scoring. Anything else the listing returns is
 * processed.
 */
function getBenchmarkImageStatus(
  image: Pick<CustomBenchmarkImage, 'id'> & Partial<Pick<CustomBenchmarkImage, 'scoringStatus'>>,
  uploads: { uploadingImageIds?: ReadonlySet<string>; failedImageIds?: ReadonlySet<string> } = {},
): BenchmarkImageStatus {
  if (uploads.failedImageIds?.has(image.id)) {
    return 'failed';
  }
  if (uploads.uploadingImageIds?.has(image.id)) {
    return 'processing';
  }
  return image.scoringStatus === 'pending' ? 'scoring' : 'ready';
}

interface UseBenchmarkImagesProps {
  workspaceId?: string;
  benchmarkId: string;
  /** A score sort needs `productCategoryId`; the API refuses it without one (422 `SORT_REQUIRES_PAIR`). */
  sortOption?: BenchmarkImageSortOption;
  /**
   * The product category whose scores the images carry; without one the tiles show no score bar. Switching keeps
   * the previous list on screen while the new scores load, so only the score bars change.
   */
  productCategoryId?: string | null;
  /**
   * Whether the category is settled; `false` holds the first request back (e.g. until the scorings are known), so
   * the list is not read once without a category and again with one.
   *
   * @default true
   */
  isReady?: boolean;
  /** Images whose upload to storage is still running, from `useBenchmarkImageUpload` */
  uploadingImageIds?: ReadonlySet<string>;
  /** Images whose upload to storage failed, from `useBenchmarkImageUpload` */
  failedImageIds?: ReadonlySet<string>;
  /**
   * Whether uploads changed the benchmark since the list was last read, from `useBenchmarkImageUpload`'s
   * `isRefreshing`: an empty list then is behind, not a benchmark without images, so it shows as loading.
   */
  isAwaitingUploads?: boolean;
  heatmap?: Heatmap;
  withArtifacts?: boolean;
  withTags?: boolean;
}

/**
 * The images of a benchmark for its page, a page at a time: the mapped images loaded so far, their grid tiles with a
 * status each (and a score each once a product category is given), how many are still uploading, the benchmark's
 * total and the scroll sentinel that loads the next page. It does not poll: `useBenchmarkScorings` watches each
 * pending scoring and re-reads these lists when one lands.
 */
function useBenchmarkImages({
  workspaceId,
  benchmarkId,
  sortOption = BenchmarkImageSortOption.Newest,
  productCategoryId,
  isReady = true,
  uploadingImageIds,
  failedImageIds,
  isAwaitingUploads = false,
  heatmap = 'original',
  withArtifacts = false,
  withTags = false,
}: UseBenchmarkImagesProps) {
  const variables: ListBenchmarkImagesQueryVariables = {
    workspace_id: workspaceId ?? '',
    benchmark_id: benchmarkId,
    ...(productCategoryId ? { product_category_id: productCategoryId } : {}),
    limit: BENCHMARK_IMAGES_PAGE_SIZE,
    ...SORT_QUERY[sortOption],
    ...(withArtifacts ? { include_artifacts: HEATMAP_ARTIFACT_TYPES } : {}),
  };
  const { data, isLoading, isError, isPlaceholderData, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
    useInfiniteQuery({
      queryKey: useListBenchmarkImagesQuery.getKey(variables),
      queryFn: ({ pageParam }) => useListBenchmarkImagesQuery.fetcher({ ...variables, offset: pageParam })(),
      initialPageParam: 0,
      // The next offset is however many images are loaded; none once that reaches the header's total.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((count, page) => count + page.images.length, 0);
        return lastPage.images.length > 0 && loaded < lastPage.totalCount ? loaded : undefined;
      },
      enabled: !!workspaceId && !!benchmarkId && isReady,
      // Switching category keeps the tiles up and only swaps their scores; no grid skeleton in between. Any other
      // change (benchmark, workspace, sort) is a different list, which must not show through. So is a category
      // switch under a score sort, which reorders the images.
      placeholderData: (previousData, previousQuery) => {
        const previous = previousQuery?.queryKey[1] as ListBenchmarkImagesQueryVariables | undefined;
        const isSameList =
          previous?.workspace_id === variables.workspace_id &&
          previous.benchmark_id === variables.benchmark_id &&
          previous.sort_by === variables.sort_by &&
          previous.sort_direction === variables.sort_direction &&
          (variables.sort_by !== 'pair_score' || previous.product_category_id === variables.product_category_id);
        return isSameList ? previousData : undefined;
      },
    });

  const pages = (data as InfiniteData<ListBenchmarkImagesQuery> | undefined)?.pages;
  const images = React.useMemo(() => pages?.flatMap((page) => page.images) ?? [], [pages]);
  // The first page's header count is the benchmark's total, whatever is loaded so far.
  const totalCount = pages?.[0]?.totalCount ?? 0;

  const loadMoreRef = useInfiniteScroll({ hasNextPage: hasNextPage ?? false, isFetchingNextPage, fetchNextPage });
  const { tagsOf } = useImageTags();

  const oldestPageFetchedAt = Math.min(...(pages ?? []).map((page) => page.fetchedAt ?? 0));
  const refreshExpiredArtifactUrls = React.useCallback(() => {
    if (Date.now() - oldestPageFetchedAt > ARTIFACT_URLS_REFRESH_AFTER_MS) {
      void refetch({ cancelRefetch: false });
    }
  }, [oldestPageFetchedAt, refetch]);

  const items = React.useMemo<BenchmarkImageItem[]>(
    () =>
      images.map((image) => {
        // Placeholder data is the previous category's list, so its scores are not this category's.
        const scored = { ...image, scoringStatus: isPlaceholderData ? null : image.scoringStatus };
        const status = getBenchmarkImageStatus(scored, { uploadingImageIds, failedImageIds });
        // Until the file is in storage its URLs only 404, so the tile asks for nothing.
        const hasFile = !uploadingImageIds?.has(image.id) && status !== 'failed';
        const heatmapSrc = hasFile && heatmap !== 'original' ? image.artifacts?.[heatmap] : null;
        const tagsWorkspaceId = withTags && hasFile ? (image.workspaceId ?? workspaceId) : undefined;
        return {
          id: image.id,
          name: image.name,
          thumbnailUrl: hasFile ? image.thumbnailUrl : null,
          largeThumbnailUrl: hasFile ? image.largeThumbnailUrl : null,
          fallbackUrl: hasFile ? image.originalUrl : null,
          status,
          ...(productCategoryId && status === 'ready'
            ? isPlaceholderData
              ? { isScoreLoading: true }
              : { score: image.vizitScore }
            : {}),
          ...(heatmapSrc && heatmap !== 'original'
            ? {
                heatmapOverlay: {
                  src: heatmapSrc,
                  alt: `${heatmapLabel(heatmap)} of ${image.name}`,
                  onError: refreshExpiredArtifactUrls,
                },
              }
            : {}),
          ...(tagsWorkspaceId ? { tagging: { workspaceId: tagsWorkspaceId, tags: tagsOf(image) } } : {}),
        };
      }),
    [
      images,
      isPlaceholderData,
      productCategoryId,
      uploadingImageIds,
      failedImageIds,
      heatmap,
      refreshExpiredArtifactUrls,
      withTags,
      workspaceId,
      tagsOf,
    ],
  );

  // Only uploads count as processing for the header; tiles waiting on a scoring are reported by the category.
  const processingCount = images.filter((image) => uploadingImageIds?.has(image.id)).length;
  // No empty state between a record's creation and the read that shows it.
  const isBehindUploads = images.length === 0 && isAwaitingUploads;

  return {
    images,
    items,
    totalCount,
    processingCount,
    // A disabled query reports isLoading false, so a workspace or category that has not resolved yet counts as loading.
    isLoading: isLoading || !workspaceId || !isReady || isBehindUploads,
    isError,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
    /** Ref for the grid's scroll sentinel; crossing it loads the next page */
    loadMoreRef,
    /** How many tiles the next page brings, for the grid's loading placeholders */
    nextPageSize: Math.min(BENCHMARK_IMAGES_PAGE_SIZE, Math.max(totalCount - images.length, 0)),
    refetch,
  };
}

export { BenchmarkImageSortOption, getBenchmarkImageStatus, isBenchmarkImageScoreSort, parseBenchmarkImageSortOption };
export { useBenchmarkImages };
